import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import API_BASE_URL from "../config";
import { ClipLoader } from "react-spinners";

const Bank = () => {
  const storedUserRaw = localStorage.getItem("user");
  const storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null;
  const token =
    storedUser?.accessToken ||
    localStorage.getItem("accessToken") ||
    "";

  const [loading, setLoading] = useState(false);
  const [existingAccount, setExistingAccount] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);


  const [bank, setBank] = useState({
    account_holder_name: "",
    bank_name: "",
    branch_name: "",
    account_number: "",
    ifsc_code: "",
    gstin: "",
    pan_number: "",
  });

  const [errors, setErrors] = useState({});

  /* -------------------- FETCH OWNER'S BANK ACCOUNT -------------------- */
  const fetchUserBanks = async () => {
    try {
      setPageLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}auth/bank-account/get`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Owners have exactly one bank account.
      const account = (res.data.data || [])[0] || null;
      setExistingAccount(account);

      if (account) {
        setBank({
          account_holder_name: account.account_holder_name || "",
          bank_name: account.bank_name || "",
          branch_name: account.branch_name || "",
          account_number: account.account_number || "",
          ifsc_code: account.ifsc_code || "",
          gstin: account.gstin || "",
          pan_number: account.pan_number || "",
        });
      }

      // No account yet -> start in edit mode so the owner can fill it in.
      // Account already saved -> start read-only until "Edit Bank Account" is clicked.
      setIsEditing(!account);

      setPageLoading(false);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load bank account"
      );
    }
  };

  useEffect(() => {
    fetchUserBanks();
  }, []);

  /* -------------------- VALIDATION -------------------- */
  const validate = () => {
    let err = {};

    if (!bank.account_holder_name) {
      err.account_holder_name = "Account holder name is required";
    } else if (!/^[A-Za-z ]+$/.test(bank.account_holder_name)) {
      err.account_holder_name = "Account holder name must contain only letters";
    }

    if (!bank.bank_name) {
      err.bank_name = "Please enter a bank name";
    }

    if (!bank.branch_name) {
      err.branch_name = "Branch name is required";
    }

    if (!bank.account_number) {
      err.account_number = "Account number is required";
    } else if (!/^[0-9]+$/.test(bank.account_number)) {
      err.account_number = "Account number must contain only numbers";
    }

    if (!bank.ifsc_code) {
      err.ifsc_code = "IFSC code is required";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifsc_code)) {
      err.ifsc_code = "Invalid IFSC code format";
    }

    if (bank.gstin && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(bank.gstin)) {
      err.gstin = "Invalid GSTIN format";
    }

    if (bank.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(bank.pan_number)) {
      err.pan_number = "Invalid PAN number format";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* -------------------- SAVE / UPDATE BANK ACCOUNT -------------------- */
  const saveBankAccount = async () => {
    try {
      if (!validate()) return;

      setLoading(true);

      const endpoint = existingAccount ? "auth/bank-account/update" : "auth/bank-account";

      await axios.post(
        `${API_BASE_URL}${endpoint}`,
        {
          account_holder_name: bank.account_holder_name,
          bank_name: bank.bank_name,
          branch_name: bank.branch_name,
          account_number: bank.account_number,
          ifsc_code: bank.ifsc_code,
          gstin: bank.gstin,
          pan_number: bank.pan_number,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success(existingAccount ? "Bank account updated successfully" : "Bank account added successfully");

      // Refresh from the server so the form reflects what was actually saved
      fetchUserBanks();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to save bank account"
      );
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }
  return (
    <section className="profilecontainer">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="profileinner">
              <div className="row">
                <Sidebar />

                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-9 col-sm-12 col-12">
                  <div className="profilerightouter">
                    <h3>Bank Details</h3>
                    <p>
                      {existingAccount
                        ? "This is the bank account you'll receive payouts to. Update the details below if anything changes."
                        : "Add the bank account you'd like to receive payouts to."}
                    </p>
                    {existingAccount?.is_verified === false && (
                      <p className="text-muted"><small>Verification pending.</small></p>
                    )}

                    <div className="row gx-2 gx-lg-4 mt-4">
                      {/* Account Holder Name */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Account Holder Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            disabled={!isEditing}
                            value={bank.account_holder_name}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                              setBank((b) => ({ ...b, account_holder_name: value }));
                            }}
                          />
                          {errors.account_holder_name && (
                            <small className="text-danger">{errors.account_holder_name}</small>
                          )}
                        </div>
                      </div>

                      {/* Bank Name */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Bank Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            disabled={!isEditing}
                            value={bank.bank_name}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                              setBank((b) => ({ ...b, bank_name: value }));
                            }}
                          />
                          {errors.bank_name && (
                            <small className="text-danger">{errors.bank_name}</small>
                          )}
                        </div>
                      </div>

                      {/* Branch Name */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Branch Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            disabled={!isEditing}
                            value={bank.branch_name}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                              setBank((b) => ({ ...b, branch_name: value }));
                            }}
                          />
                          {errors.branch_name && (
                            <small className="text-danger">{errors.branch_name}</small>
                          )}
                        </div>
                      </div>

                      {/* Account Number */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Account Number *</label>
                          <input
                            type="text"
                            className="form-control"
                            disabled={!isEditing}
                            value={bank.account_number}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, "");
                              setBank((b) => ({ ...b, account_number: value }));
                            }}
                          />
                          {errors.account_number && (
                            <small className="text-danger">{errors.account_number}</small>
                          )}
                        </div>
                      </div>

                      {/* IFSC Code */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>IFSC Code *</label>
                          <input
                            type="text"
                            className="form-control"
                            maxLength={11}
                            disabled={!isEditing}
                            value={bank.ifsc_code}
                            onChange={(e) => {
                              const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, ""); // allow only letters + numbers
                              setBank((b) => ({ ...b, ifsc_code: value }));
                            }}
                          />
                          {errors.ifsc_code && (
                            <small className="text-danger">{errors.ifsc_code}</small>
                          )}
                        </div>
                      </div>

                      {/* GSTIN */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>GSTIN (If Registered)</label>
                          <input
                            type="text"
                            className="form-control"
                            maxLength={15}
                            disabled={!isEditing}
                            value={bank.gstin}
                            onChange={(e) => {
                              const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "");
                              setBank((b) => ({ ...b, gstin: value }));
                            }}
                          />
                          {errors.gstin && (
                            <small className="text-danger">{errors.gstin}</small>
                          )}
                        </div>
                      </div>

                      {/* PAN Number */}
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>PAN Number</label>
                          <input
                            type="text"
                            className="form-control"
                            maxLength={10}
                            disabled={!isEditing}
                            value={bank.pan_number}
                            onChange={(e) => {
                              const value = e.target.value
                                .toUpperCase()
                                .replace(/[^A-Z0-9]/g, "");
                              setBank((b) => ({ ...b, pan_number: value }));
                            }}
                          />
                          {errors.pan_number && (
                            <small className="text-danger">{errors.pan_number}</small>
                          )}
                        </div>
                      </div>

                      {/* Submit / Edit toggle */}
                      <div className="col-12 mt-3">
                        {isEditing ? (
                          <button
                            className="theme-btn"
                            onClick={saveBankAccount}
                            disabled={loading}
                          >
                            {loading
                              ? "Saving..."
                              : existingAccount
                              ? "Update Bank Account"
                              : "Save Bank Account"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="theme-btn"
                            onClick={() => setIsEditing(true)}
                          >
                            Edit Bank Account
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* END RIGHT */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Bank;
